import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import DemoGuideCard, { type DemoGuideItem } from '@/pages/demo/components/DemoGuideCard';

const ITEMS: DemoGuideItem[] = [
  {
    step: '1',
    title: 'Homepage',
    description: 'The full masthead: top stories, upcoming tournaments, the community bulletin board and the sponsor strip.',
    to: '/',
    icon: 'ri-home-4-line',
  },
  {
    step: '2',
    title: 'Tournament Calendar',
    description: 'Every local tournament grouped by month, with event detail pages and a free event submission form.',
    to: '/calendar',
    icon: 'ri-calendar-event-line',
  },
  {
    step: '3',
    title: 'News & Stories',
    description: 'The editorial library — community features, charity spotlights and course coverage from Long Beach.',
    to: '/news',
    icon: 'ri-newspaper-line',
  },
  {
    step: '4',
    title: 'Community Submissions',
    description: 'The bulletin board where readers post community notices, and the event listing form on the calendar page.',
    to: '/#bulletin',
    icon: 'ri-pushpin-2-line',
  },
  {
    step: '5',
    title: 'Sponsorship Opportunities',
    description: 'The sponsorship packages, audience numbers and the advertiser enquiry form.',
    to: '/partners',
    icon: 'ri-megaphone-line',
  },
  {
    step: '6',
    title: 'AI Workflow Concept',
    description: 'A conceptual preview of the planned AI-assisted publishing workflow — clearly labelled as not yet operational.',
    to: '/how-it-works',
    icon: 'ri-flask-line',
    tag: 'Concept preview',
  },
  {
    step: '7',
    title: 'Management Preview',
    description: 'A front-end demonstration of the future editorial dashboard — how the team will review, approve and publish.',
    to: '/demo-admin',
    icon: 'ri-dashboard-3-line',
    tag: 'Demo preview',
  },
  {
    step: '8',
    title: 'Revenue & Expansion Demo',
    description: 'An interactive concept for sponsorship sales, tournament monetisation and multi-region publishing — sample data only.',
    to: '/demo/revenue',
    icon: 'ri-line-chart-line',
    tag: 'Concept preview',
  },
  {
    step: '9',
    title: 'Multilingual & Inclusive Demo',
    description: 'A sample article in English and Spanish, independent reader preferences and inclusive community categories — clearly labelled as a preview.',
    to: '/demo/multilingual',
    icon: 'ri-translate-2',
    tag: 'Concept preview',
  },
];

export default function DemoGuidePage() {
  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Header />
      <main>
        <section className="relative bg-foreground-950 pt-28 md:pt-40 pb-14 md:pb-20 px-4 md:px-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-foreground-950 to-foreground-950"></div>
          <div className="relative">
            <p className="gs-rise font-label uppercase tracking-[0.25em] text-sm text-accent-400 font-bold">Demo guide</p>
            <h1 className="gs-rise-2 font-heading font-extrabold uppercase text-background-50 text-4xl md:text-7xl leading-[0.94] tracking-tight mt-2 max-w-4xl">
              GreenSide News walkthrough
            </h1>
            <p className="gs-rise-3 mt-5 text-background-200 text-base md:text-lg max-w-2xl">
              A presentation guide for the client meeting. Use these links to move through the full site in a guided order.
            </p>
          </div>
        </section>

        <section className="bg-background-50 px-4 md:px-8 py-16 md:py-20">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">Nine stops</p>
            <span className="bg-secondary-100 text-secondary-900 border border-secondary-200 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm">Presentation only</span>
          </div>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground-950 leading-[1.05]">Where to go in the demo</h2>
          <p className="mt-4 text-foreground-600 max-w-2xl">
            Each card opens a section of the live site. Work through them in order for a complete tour.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {ITEMS.map((item) => (
              <DemoGuideCard key={item.title} item={item} />
            ))}
          </div>
        </section>

        <section className="bg-background-100 border-y border-background-200 px-4 md:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">Suggested click-path</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground-950 leading-[1.05] mt-3">A 15-minute run order</h2>
              <p className="mt-4 text-foreground-600">
                Open the homepage and let the client scroll naturally, then step through each stop. This order tells the story from public site to behind-the-scenes tooling.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-play-line"></i></span>
                  Start the walkthrough
                </Link>
                <Link to="/how-it-works" className="inline-flex items-center gap-2 border border-background-300 hover:border-primary-400 text-foreground-800 font-label font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-flask-line"></i></span>
                  Jump to AI concept
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ol className="rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200 overflow-hidden">
                {ITEMS.map((item) => (
                  <li key={item.title}>
                    <Link to={item.to} className="group flex items-center gap-4 px-5 md:px-6 py-4 hover:bg-background-100 transition-colors cursor-pointer">
                      <span className="w-8 h-8 rounded-md bg-foreground-950 text-accent-400 flex items-center justify-center font-label font-bold text-sm shrink-0">{item.step}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-heading font-bold text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">{item.title}</span>
                        <span className="block text-xs text-foreground-500 truncate">{item.to}</span>
                      </span>
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-hover:text-primary-500 transition-transform duration-300 group-hover:translate-x-1 shrink-0"><i className="ri-arrow-right-line"></i></span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="bg-background-50 px-4 md:px-8 py-14 md:py-20">
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 md:p-8">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-md bg-secondary-500 text-foreground-950 flex items-center justify-center text-xl shrink-0"><i className="ri-information-line"></i></span>
              <div>
                <h3 className="font-heading font-bold text-2xl text-foreground-950">About this guide</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
                  <li className="flex items-start gap-3"><span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-focus-3-line"></i></span>This page is a presentation aid for the client walkthrough and is not linked from the main site navigation.</li>
                  <li className="flex items-start gap-3"><span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-flask-line"></i></span>The AI Workflow Concept is a conceptual preview &mdash; the automation it describes is not yet operational.</li>
                  <li className="flex items-start gap-3"><span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-dashboard-3-line"></i></span>The Management Preview is a front-end demonstration; any actions taken there are illustrative and are not saved.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}